import {
  Button,
  Loader,
  Text,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import * as turf from "@turf/turf";
import { bearing, point } from "@turf/turf";
import maplibregl, { MapLibreEvent } from "maplibre-gl";
import { default as React, useEffect, useRef, useState } from "react";
import Map, {
  FullscreenControl,
  GeolocateControl,
  MapRef,
} from "react-map-gl/maplibre";
import jpImage from "../../assets/jordan-peterson.png";
import clownImage from "../../assets/clown.png";
import { HiExclamationTriangle } from "react-icons/hi2";
import { HiLocationMarker } from "react-icons/hi";

const mapStyleJsons = {
  positron: "https://tiles.openfreemap.org/styles/positron",
};

export default function JpView() {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const colorScheme = useComputedColorScheme();
  const mapRef = useRef<MapRef>(null);
  const [animationData, setAnimationData] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocatingJp, setIsLocatingJp] = useState(true);
  const [jpPoint, setJpPoint] = useState(null);
  const [isJpComing, setIsJpComing] = useState(false);
  const [isJpHere, setIsJpHere] = useState(false);

  function getRandomInRange(from, to, fixed) {
    return (Math.random() * (to - from) + from).toFixed(fixed) * 1;
    // .toFixed() returns string, so ' * 1' is a trick to convert to number
  }

  const [data, setData] = useState({
    origin: [getRandomInRange(-180, 180, 3), getRandomInRange(-70, 70, 3)],
    destination: [null, null],
  });

  const TIME_TO_LOCATE_JP = 10000;
  const STEPS = 20000;
  const ANIMATION_DURATION = 60000;

  useEffect(() => {
    if (userLocation != null) {
      const data2 = {
        origin: [data.origin[0], data.origin[1]],
        destination: [userLocation.latitude, userLocation.longitude],
      };
      setData(data2);
      initDestinationPosition([userLocation.longitude, userLocation.latitude]);
      initializeTravelAnimation(data2);
    }
  }, [userLocation]);

  const onLoadMap = () => {
    setTimeout(() => {
      setIsLocatingJp(false);
      initJpPosition();

      mapRef.current.flyTo({
        center: {
          lat: data.origin[1],
          lng: data.origin[0],
        },
        zoom: 2,
        duration: 2000,
      });
    }, TIME_TO_LOCATE_JP);
  };

  const getUserLocation = () => {
    // if geolocation is supported by the users browser
    if (navigator.geolocation) {
      // get the current users location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // save the geolocation coordinates in two variables
          const { latitude, longitude } = position.coords;
          // update the value of userlocation variable
          setUserLocation({ latitude, longitude });
        },
        // if there was an error getting the users location
        (error) => {
          console.error("Error getting user location:", error);
        }
      );
    }
    // if geolocation is not supported by the users browser
    else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const initJpPosition = () => {
    const map = mapRef.current?.getMap();
    const htmlImage = new Image();
    htmlImage.src = jpImage;
    htmlImage.onload = () => {
      map.addImage("jp", htmlImage);

      const { origin } = data;

      // A single point that animates along the route.
      // Coordinates are initially set to origin.
      const point: any = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Point",
              coordinates: origin,
            },
          },
        ],
      };

      setJpPoint(point);

      map.addSource("point", {
        type: "geojson",
        data: point as any,
      });

      map.addLayer({
        id: "points",
        type: "symbol",
        source: "point", // reference the data source
        layout: {
          "icon-image": "jp", // reference the image
          "icon-size": 0.8,
          "icon-rotation-alignment": "map",
          "icon-overlap": "always",
          "icon-ignore-placement": true,
        },
      });
    };
  };

  const initDestinationPosition = (destination: number[]) => {
    const map = mapRef.current?.getMap();
    const htmlImage = new Image();
    htmlImage.src = clownImage;
    htmlImage.onload = () => {
      console.log("clownImage loaded");
      map.addImage("clown", htmlImage);

      // A single point that animates along the route.
      // Coordinates are initially set to origin.
      const point: any = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Point",
              coordinates: destination,
            },
          },
        ],
      };

      map.addSource("point2", {
        type: "geojson",
        data: point as any,
      });

      map.addLayer({
        id: "points2",
        type: "symbol",
        source: "point2", // reference the data source
        layout: {
          "icon-image": "clown", // reference the image
          "icon-size": 0.5,
          "icon-rotation-alignment": "map",
          "icon-overlap": "always",
          "icon-ignore-placement": true,
        },
      });

      map.flyTo({
        center: {
          lat: destination[1],
          lng: destination[0],
        },
        zoom: 2,
        duration: 1000,
      });
    };
  };

  const initializeTravelAnimation = (dataP: any) => {
    const map = mapRef.current?.getMap();

    if (!map) return;

    const { origin, destination } = dataP;

    // A simple line from origin to destination.
    const route = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [origin, [destination[1], destination[0]]],
          },
        },
      ],
    };

    // Calculate the distance in kilometers between route start/end point.
    const lineDistance = turf.distance(
      route.features[0].geometry.coordinates[0],
      route.features[0].geometry.coordinates[1],
      { units: "kilometers" }
    );

    const arc = [];

    // Number of steps to use in the arc and animation, more steps means
    // a smoother arc and animation, but too many steps will result in a
    // low frame rate
    const steps = STEPS;

    // Define line
    const line = turf.lineString(route.features[0].geometry.coordinates, {
      units: "kilometers",
    });

    // Draw an arc between the `origin` & `destination` of the two points
    for (let i = 0; i < lineDistance; i += lineDistance / steps) {
      const segment = turf.along(line, i, { units: "kilometers" });
      arc.push(segment.geometry.coordinates);
    }

    // Update the route with calculated arc coordinates
    route.features[0].geometry.coordinates = arc;

    // Add a source and layer displaying a point which will be animated in a circle.
    map.addSource("route", {
      type: "geojson",
      data: route as any,
    });

    if (false) {
      map.addLayer({
        id: "route",
        source: "route",
        type: "line",
        paint: {
          "line-width": 2,
          "line-color": "#007cbf",
        },
      });
    }

    setAnimationData({ map, route, steps });
  };

  const calculateStepByDurationPercentage = (durationPercentage: number) => {
    // return Math.floor(durationPercentage * STEPS); // Linear interpolation
    const B = -5.8;
    const C = -6.2;
    const percentOfTrajectory =
      Math.E ** (B * Math.E ** (C * durationPercentage));
    const step = Math.floor(percentOfTrajectory * STEPS);
    return step;
  };

  const triggerTravelAnimation = () => {
    setIsJpComing(true);

    let { map, route } = animationData;
    const point = jpPoint;

    let startTime = performance.now();
    let elapsedTime = 0;

    function animate(currentTime) {
      elapsedTime = currentTime - startTime;

      if (elapsedTime >= ANIMATION_DURATION) {
        setIsJpComing(false);
        setIsJpHere(true);
        point.features[0].geometry.coordinates = [
          data.destination[1],
          data.destination[0],
        ];
        map.removeLayer("points2");
        (map.getSource("point") as maplibregl.GeoJSONSource).setData(point);
        return;
      }

      // Update point geometry to a new position based on counter denoting
      // the index to access the arc.
      const step = calculateStepByDurationPercentage(
        elapsedTime / ANIMATION_DURATION
      );

      point.features[0].geometry.coordinates =
        route.features[0].geometry.coordinates[step];

      // Calculate the bearing to ensure the icon is rotated to match the route arc
      // The bearing is calculate between the current point and the next point, except
      // at the end of the arc use the previous point and the current point
      /*(point.features[0].properties as any).bearing = turf.bearing(
        turf.point(
          route.features[0].geometry.coordinates[
            counter >= steps ? counter - 1 : counter
          ]
        ),
        turf.point(
          route.features[0].geometry.coordinates[
            counter >= steps ? counter : counter + 1
          ]
        )
      );*/

      // Update the source with this new data.
      (map.getSource("point") as maplibregl.GeoJSONSource).setData(point);

      // Request the next frame of animation so long the end has not been reached.
      if (elapsedTime < ANIMATION_DURATION) {
        requestAnimationFrame(animate);
      }
    }

    // Start the animation.
    requestAnimationFrame(animate);
  };

  return (
    <div
      className={
        "flex flex-col w-full h-full gap-4 p-4 " +
        (isJpHere ? "animate-pulse bg-red-900" : "")
      }
    >
      <Title size={20} ta={"center"}>
        <Text c={"red"} size="xl" fw={500}>
          JORDAN PETERSON
        </Text>
        <Text c={"dimmed"}>TRACKING SYSTEM</Text>
      </Title>
      <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden">
        <Map
          id="mapData2"
          reuseMaps
          initialViewState={{
            longitude: 0,
            latitude: 0,
            zoom: 1,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle={mapStyleJsons.positron}
          onLoad={(event: MapLibreEvent) => {
            onLoadMap();
          }}
          interactive={true}
          projection={"globe"}
          attributionControl={false}
          ref={mapRef}
        >
          <FullscreenControl position="top-left" />
        </Map>
      </div>

      {isLocatingJp && (
        <div className="w-full flex justify-center items-center gap-2">
          <Loader color="yellow" size="sm"></Loader>
          <Text fz="md" c="yellow" ta={"center"}>
            LOCATING JORDAN PETERSON...
          </Text>
        </div>
      )}

      {!isLocatingJp && userLocation == null && (
        <Button
          color="yellow"
          variant="light"
          leftSection={<HiLocationMarker />}
          onClick={getUserLocation}
        >
          SET TARGET LOCATION
        </Button>
      )}

      {!isJpHere && !isJpComing && !isLocatingJp && userLocation != null && (
        <Button
          color="red"
          leftSection={<HiExclamationTriangle />}
          onClick={triggerTravelAnimation}
        >
          CALL JORDAN PETERSON
        </Button>
      )}

      {isJpComing && (
        <Text fz="md" c="red" ta={"center"}>
          JORDAN PETERSON IS COMING...
        </Text>
      )}

      {isJpHere && (
        <Text fz="md" c="red" ta={"center"}>
          JORDAN PETERSON IS HERE
        </Text>
      )}
    </div>
  );
}
